import React, { useState, useEffect } from 'react';
import './PDPSubNav.scss';
import CTAButton from '../../UI/CTA/CTAButton/CTAButton';
import { BREAKPOINTS } from '../../../constants/breakpoints';
import Rating from '../../UI/Rating/Rating';
import Loader from '../../UI/Icons/Loader/Loader';

const DESKTOP_WIDTH = BREAKPOINTS.large;

const BuyNowLabel = ({ product }) => {
  // Hack
  const discountedPrice = product.compareAtPrice;
  const price = discountedPrice || product.price;
  return (
    <span>
      Buy Now
      <span className="c-pdp-subnav__price">{price}</span>
      {discountedPrice && <span className="c-pdp-subnav__original-price">{product.price}</span>}
    </span>
  );
};
/**
 * Render the sub nav on PDP
 * @param {Object} product - Object containing information about product to display in the subnav header
 * @param {Boolean} show_talk_to_sales - Option to show/hide talk to sales
 * @param {Boolean} show_reviews - Option to show/hide reviews
 * @param {Boolean} show_title - Show/hide title
 * @param {Array} jump_links - Array containing jump links for dropdown
 * @param {string} star_asset - Stars image for reviews
 * @param {function} addToCart - Redux dispatch function to add items to cart
 * @param {boolean} announcementActive
 * @param {boolean} enablePreorder
 * @param {string} anchorLink
 * @returns {JSX.Element}
 * @constructor
 */
const PDPSubNav = ({
  product,
  show_talk_to_sales,
  show_reviews,
  show_title,
  jump_links,
  star_asset,
  addToCart,
  announcementActive,
  enablePreorder = false,
  anchorLink,
}) => {
  const [scrollDirection, setScrollDirection] = useState('');
  const [scrollPos, setScrollPos] = useState(window.scrollY);
  const [isMobile, setIsMobile] = useState(window.innerWidth < DESKTOP_WIDTH);
  const [isHeaderHidden, setIsHeaderHidden] = useState(window.scrollY >= 105);
  const [fetching, setFetching] = useState(false);

  const [announcementBar, setAnnouncementBar] = useState(
    document.querySelector('.c-announcement-bar'),
  );

  useEffect(() => {
    setIsMobile(window.innerWidth < DESKTOP_WIDTH);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', scroll);

    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener('click', (e) => {
        e.preventDefault();
        document.querySelector(anchor.getAttribute('href')).scrollIntoView({
          behavior: 'smooth',
        });
      });
    });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', scroll);
    };
  }, [scrollPos, scrollDirection, announcementBar]);

  const addAction = async () => {
    const properties = {
      quantity: 1,
      id: product?.variants[0]?.id,
    };

    setFetching(true);
    await addToCart(properties, product?.title)
      .then(() => setFetching(false))
      .catch(() => {
        setFetching(false);
        console.error('Error fetching, please try again');
      });
  };

  const scroll = (e) => {
    const window = e.currentTarget;
    if (scrollPos > window.scrollY) setScrollDirection('up');
    else if (scrollPos < window.scrollY) setScrollDirection('down');
    setScrollPos(window.scrollY);

    const headerHeight = isMobile ? 50 : 105;
    setIsHeaderHidden(window.scrollY >= headerHeight);
  };

  const handleResize = () => setIsMobile(window.innerWidth < DESKTOP_WIDTH);

  const handleSelect = (e) => (window.location.hash = e.target.value);

  const renderJumpLinks = () =>
    jump_links.length >= 1 ? (
      <select
        className="c-pdp-subnav__options color-black body-3"
        onChange={(e) => handleSelect(e)}
      >
        {jump_links.map((link) => (
          <option value={link.anchor}>{link.label}</option>
        ))}
      </select>
    ) : null;

  const setTopPosition = () => {
    if (isMobile) {
      if (announcementActive) {
        if (scrollDirection === 'up') {
          if (window.scrollY <= announcementBar.offsetHeight + 48 && window.scrollY > 48) {
            return 48;
          } else if (window.scrollY <= announcementBar.offsetHeight + 48) {
            return announcementBar.offsetHeight + 48;
          }

          return 48;
        } else if (scrollDirection === 'down' && window.scrollY >= 48) {
          return 0;
        }

        return announcementBar.offsetHeight + 48;
      } else {
        if (scrollDirection === 'up') {
          return 48;
        } else if (scrollDirection === 'down' && window.scrollY >= 48) {
          return 0;
        }

        return 48;
      }
    } else {
      if (announcementActive) {
        if (scrollDirection === 'up') {
          if (window.scrollY <= announcementBar.offsetHeight + 96 && window.scrollY > 96) {
            return 96;
          } else if (window.scrollY <= announcementBar.offsetHeight + 96) {
            return announcementBar.offsetHeight + 96;
          }

          return 96;
        } else if (scrollDirection === 'down' && window.scrollY >= 96) {
          return 0;
        }

        return announcementBar.offsetHeight + 96;
      } else {
        if (scrollDirection === 'up') {
          return 96;
        } else if (scrollDirection === 'down' && window.scrollY >= 96) {
          return 0;
        }

        return 96;
      }
    }
  };

  return (
    <div
      className={`c-pdp-subnav background-color-gray ${
        isHeaderHidden ? '' : isMobile ? 'mobile' : 'desktop'
      }`}
    >
      <div style={{ top: `${setTopPosition()}px` }} className="c-pdp-subnav__top">
        <div className="c-pdp-subnav__content">
          {isMobile ? (
            <>
              {show_title && (
                <h3 className="c-pdp-subnav__heading color-black h3-roobert">{product?.title}</h3>
              )}
              {renderJumpLinks()}
            </>
          ) : (
            <>
              <div className="c-pdp-subnav__leading">
                {show_title && (
                  <h3 className="c-pdp-subnav__heading color-black h3-roobert">{product?.title}</h3>
                )}
                {renderJumpLinks()}
              </div>

              <div className="c-pdp-subnav__trailing">
                <div className="c-pdp-subnav__rating-container">
                  <div className="c-pdp-subnav__rating body-3">
                    {show_reviews && product?.rating?.count && (
                      <Rating
                        showCount={true}
                        count={product?.rating?.count}
                        stars={product?.rating?.stars}
                        starsAssetUrl={star_asset}
                      />
                    )}
                  </div>
                  <span className="c-pdp-subnav__warranty eyebrow-2">{product?.warranty}</span>
                </div>
                <div className="c-pdp-subnav__buy-now-btn">
                  <CTAButton
                    isButton={!enablePreorder}
                    onClick={enablePreorder ? null : addAction}
                    ariaLabel={
                      enablePreorder ? (
                        'Talk to Sales'
                      ) : product?.variants[0]?.available ? (
                        <BuyNowLabel product={product} />
                      ) : (
                        'Sold Out'
                      )
                    }
                    type="primary"
                    text={
                      enablePreorder ? (
                        'Talk to Sales'
                      ) : product?.variants[0]?.available ? (
                        fetching ? (
                          <Loader lightMode={true} />
                        ) : (
                          <BuyNowLabel product={product} />
                        )
                      ) : (
                        'Sold Out'
                      )
                    }
                    additionalClasses="cta"
                    link={enablePreorder ? anchorLink : ''}
                    disabled={!product?.variants[0]?.available}
                  />
                </div>
                {show_talk_to_sales && (
                  <div className="c-pdp-subnav__talk-to-sales-btn">
                    <CTAButton text="Talk to Sales" additionalClasses="cta" link="#preorder-form" />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {isMobile ? (
        <div className="c-pdp-subnav__bottom">
          <div className="c-pdp-subnav__rating body-3">
            {show_reviews && product?.rating?.count && (
              <Rating
                showCount={true}
                count={product?.rating?.count}
                stars={product?.rating?.stars}
                starsAssetUrl={star_asset}
              />
            )}
          </div>
          <div className="c-pdp-subnav__buy-now-btn">
            <CTAButton
              isButton={!enablePreorder}
              onClick={enablePreorder ? null : addAction}
              ariaLabel={
                enablePreorder ? (
                  'Talk to Sales'
                ) : product?.variants[0]?.available ? (
                  <BuyNowLabel product={product} />
                ) : (
                  'Sold Out'
                )
              }
              type="primary"
              text={
                enablePreorder ? (
                  'Talk to Sales'
                ) : product?.variants[0]?.available ? (
                  fetching ? (
                    <Loader lightMode={true} />
                  ) : (
                    <BuyNowLabel product={product} />
                  )
                ) : (
                  'Sold Out'
                )
              }
              additionalClasses="cta"
              link={enablePreorder ? anchorLink : ''}
              disabled={!product?.variants[0]?.available}
            />
          </div>
          {show_talk_to_sales && (
            <div className="c-pdp-subnav__talk-to-sales-btn">
              <CTAButton text="Talk to Sales" additionalClasses="cta" link="#preorder-form" />
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default PDPSubNav;
